import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { cn } from "@win-reality/react-ui";

import { JerseyNumber } from "@/components/EventForm/Lineup/JerseyNumber";

type LineupListProps = {
  /** Players currently in the batting order */
  lineupPlayers: IPlayer[];
  /** Players available to add to the lineup */
  availablePlayers: IPlayer[];
  /** Callback when a player is added to the lineup */
  onAddToLineup: (player: IPlayer) => void;
  /** Callback when a player is removed from the lineup */
  onRemoveFromLineup: (player: IPlayer) => void;
  /** Callback when the lineup order changes */
  onReorder: (newLineup: IPlayer[]) => void;
};

/**
 * LineupList - Unified batting order list with drag-and-drop reordering
 *
 * Shows lineup players (reorderable) followed by available players.
 * Uses @dnd-kit for smooth touch-friendly drag and drop.
 */
export const LineupList = ({
  lineupPlayers,
  availablePlayers,
  onAddToLineup,
  onRemoveFromLineup,
  onReorder,
}: LineupListProps) => {
  // Configure sensors for different input types
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200, // 200ms delay before drag starts on touch
        tolerance: 5, // 5px tolerance for touch movement
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = lineupPlayers.findIndex((p) => p.uuid === active.id);
      const newIndex = lineupPlayers.findIndex((p) => p.uuid === over.id);

      const newLineup = arrayMove(lineupPlayers, oldIndex, newIndex);
      onReorder(newLineup);
    }
  };

  return (
    <div className="space-y-2">
      {/* Sortable lineup players */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={lineupPlayers.map((p) => p.uuid)}
          strategy={verticalListSortingStrategy}
        >
          {lineupPlayers.map((player, index) => (
            <SortableLineupRow
              key={player.uuid}
              player={player}
              position={index + 1}
              onRemove={() => onRemoveFromLineup(player)}
            />
          ))}
        </SortableContext>
      </DndContext>

      {/* Available players (not in lineup) */}
      {availablePlayers.map((player) => (
        <AvailablePlayerRow
          key={player.uuid}
          player={player}
          onAdd={() => onAddToLineup(player)}
        />
      ))}
    </div>
  );
};

/**
 * SortableLineupRow - A draggable row for players in the lineup
 */
type SortableLineupRowProps = {
  player: IPlayer;
  position: number;
  onRemove: () => void;
};

const SortableLineupRow = ({
  player,
  position,
  onRemove,
}: SortableLineupRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: player.uuid });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const displayName = `${player.firstName} ${player.lastName}`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 rounded border border-gray-200 bg-white px-2 py-2 transition-shadow",
        isDragging && "z-50 shadow-lg",
      )}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab touch-none rounded p-1 text-accent-dark-3 hover:bg-gray-100 active:cursor-grabbing"
        aria-label="Drag to reorder"
      >
        <Bars3Icon className="h-5 w-5" />
      </button>

      {/* Position badge */}
      <div
        className={cn(
          // Size
          "h-8 w-8 shrink-0",
          // Layout
          "flex items-center justify-center",
          // Border
          "rounded",
          // Background
          "bg-badge-batter",
          // Text
          "text-sm font-semibold text-white",
        )}
      >
        {position}
      </div>

      {/* Player name */}
      <span className="flex-1 text-sm font-medium text-text-light">
        <JerseyNumber jerseyNumber={player.jerseyNumber} />
        {displayName}
      </span>

      {/* Remove button */}
      <button
        onClick={onRemove}
        className="rounded p-1 text-accent-dark-3 transition-colors hover:bg-gray-100 hover:text-text-light"
        aria-label={`Remove ${displayName} from lineup`}
      >
        <XMarkIcon className="h-5 w-5" />
      </button>
    </div>
  );
};

/**
 * AvailablePlayerRow - A row for players not yet in the lineup
 */
type AvailablePlayerRowProps = {
  player: IPlayer;
  onAdd: () => void;
};

const AvailablePlayerRow = ({ player, onAdd }: AvailablePlayerRowProps) => {
  const displayName = `${player.firstName} ${player.lastName}`;

  return (
    <div className="flex items-center gap-3 rounded border border-gray-200 bg-white px-2 py-2">
      {/* Inactive drag handle (visual consistency) */}
      <div className="rounded p-1 text-gray-300">
        <Bars3Icon className="h-5 w-5" />
      </div>

      {/* Empty position badge */}
      <div
        className={cn(
          // Size
          "h-8 w-8 shrink-0",
          // Layout
          "flex items-center justify-center",
          // Border
          "rounded",
          // Background
          "bg-gray-100",
          // Text
          "text-sm font-medium text-gray-400",
        )}
      >
        --
      </div>

      {/* Player name */}
      <span className="flex-1 text-sm font-medium text-text-light">
        <JerseyNumber jerseyNumber={player.jerseyNumber} />
        {displayName}
      </span>

      {/* Add button */}
      <button
        onClick={onAdd}
        className="text-sm font-medium text-blue hover:underline"
      >
        Add Batter
      </button>
    </div>
  );
};
